import { motion } from "framer-motion";
import {
    Activity,
    Award,
    BarChart3,
    BookmarkPlus,
    CalendarCheck,
    CheckCircle,
    Clock,
    Flame,
    Loader2,
    MapPin,
    PhoneCall,
    Sparkles,
    Target,
    TrendingUp,
    UserCheck,
    Users,
    XCircle,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { api } from "../lib/api";
import { initials } from "../lib/format";

const PRESETS = [
    { value: 7, label: "7 jours" },
    { value: 30, label: "30 jours" },
    { value: 90, label: "3 mois" },
    { value: 180, label: "6 mois" },
];

function KpiTile({ label, value, suffix, icon: Icon, tone = "orange", subtle }) {
    const tones = {
        orange: "from-shariti-orange to-shariti-orange-light text-white",
        emerald: "from-emerald-500 to-emerald-400 text-white",
        amber: "from-amber-500 to-amber-400 text-white",
        red: "from-red-500 to-red-400 text-white",
        slate: "from-slate-700 to-slate-500 text-white",
        purple: "from-purple-600 to-purple-400 text-white",
        blue: "from-blue-600 to-blue-400 text-white",
    };
    return (
        <div className="card-soft p-4 md:p-5 relative overflow-hidden" data-testid={`kpi-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            <div className="flex items-start justify-between gap-3">
                <div>
                    <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                        {label}
                    </div>
                    <div className="mt-1 flex items-baseline gap-1">
                        <span className="text-3xl md:text-4xl font-black tracking-tight text-shariti-black tabular-nums">
                            {value}
                        </span>
                        {suffix && (
                            <span className="text-base font-extrabold text-slate-400">{suffix}</span>
                        )}
                    </div>
                    {subtle && (
                        <div className="mt-1 text-[11px] font-semibold text-slate-500">{subtle}</div>
                    )}
                </div>
                <div className={`h-10 w-10 rounded-2xl bg-gradient-to-br ${tones[tone]} flex items-center justify-center shrink-0 shadow-btn`}>
                    <Icon className="h-5 w-5" />
                </div>
            </div>
        </div>
    );
}

function Sparkbar({ value, max, color = "bg-shariti-orange" }) {
    const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
    return (
        <div className="h-1.5 w-full rounded-full bg-shariti-cream overflow-hidden">
            <div className={`h-full ${color} rounded-full transition-all`} style={{ width: `${pct}%` }} />
        </div>
    );
}

function UserRow({ person, kind, isTop }) {
    const m = person.metrics;
    return (
        <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className={`card-soft overflow-hidden ${isTop ? "ring-2 ring-shariti-orange/40" : ""}`}
            data-testid={`team-row-${kind}-${person.user_id}`}
        >
            <div className="p-4 md:p-5 grid grid-cols-1 lg:grid-cols-[1fr_3fr] gap-4 md:gap-6 items-center">
                <div className="flex items-center gap-3 min-w-0">
                    {person.avatar_url ? (
                        <img src={person.avatar_url} alt="" className="h-11 w-11 rounded-2xl object-cover" />
                    ) : (
                        <div className={`h-11 w-11 rounded-2xl ${kind === "pre_recruteur" ? "bg-shariti-orange-soft text-shariti-orange" : "bg-blue-100 text-blue-700"} font-extrabold flex items-center justify-center`}>
                            {initials(person.name)}
                        </div>
                    )}
                    <div className="min-w-0">
                        <div className="flex items-center gap-2">
                            <div className="font-extrabold text-shariti-black truncate">{person.name}</div>
                            {isTop && (
                                <span className="inline-flex items-center gap-0.5 text-[9px] font-extrabold uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200">
                                    <Award className="h-2.5 w-2.5" /> Top
                                </span>
                            )}
                        </div>
                        <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-widest mt-0.5 truncate">
                            {kind === "pre_recruteur" ? "Pré-recruteur" : "Référent terrain"}
                        </div>
                    </div>
                </div>

                {kind === "pre_recruteur" ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                        <Stat label="Assignés" value={m.assigned} icon={Users} />
                        <Stat label="Pré-rec faits" value={m.prerec_done} icon={PhoneCall} highlight />
                        <Stat label="Immersions" value={m.immersion_scheduled} icon={CalendarCheck} />
                        <Stat label="Validés" value={m.validated} icon={CheckCircle} />
                        <Stat label="Conv. pré-rec" value={`${m.conv_rate_prerec}%`} icon={TrendingUp} tone="purple" />
                        <Stat label="Score moyen" value={m.avg_ai_score || "—"} icon={Sparkles} tone="amber" />
                    </div>
                ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                        <Stat label="Programmées" value={m.scheduled} icon={CalendarCheck} />
                        <Stat label="Confirmées" value={m.confirmed} icon={UserCheck} />
                        <Stat label="Réalisées" value={m.done} icon={MapPin} highlight />
                        <Stat label="Validés" value={m.validated} icon={CheckCircle} />
                        <Stat label="No-show" value={m.no_show} icon={XCircle} tone="red" />
                        <Stat label="Taux présence" value={`${m.show_rate}%`} icon={TrendingUp} tone="purple" />
                    </div>
                )}
            </div>
            {/* Progress bar visualising the most meaningful metric for the role */}
            <div className="px-4 md:px-5 pb-4">
                {kind === "pre_recruteur" ? (
                    <Sparkbar value={m.prerec_done} max={Math.max(m.assigned, 1)} />
                ) : (
                    <Sparkbar value={m.done} max={Math.max(m.scheduled, 1)} color="bg-blue-500" />
                )}
            </div>
        </motion.div>
    );
}

function Stat({ label, value, icon: Icon, highlight, tone = "slate" }) {
    const palette = {
        slate: "text-slate-600",
        purple: "text-purple-600",
        amber: "text-amber-600",
        red: "text-red-600",
    };
    return (
        <div className="min-w-0">
            <div className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-slate-400">
                <Icon className="h-3 w-3 shrink-0" />
                <span className="truncate">{label}</span>
            </div>
            <div className={`mt-0.5 text-xl font-black ${highlight ? "text-shariti-orange" : palette[tone]} tabular-nums`}>
                {value ?? 0}
            </div>
        </div>
    );
}

function StatusBar({ statuses }) {
    const palette = {
        new: { label: "Nouveau", color: "bg-blue-400" },
        prerec_assigned: { label: "À rappeler", color: "bg-amber-300" },
        prerec_done: { label: "Pré-rec fait", color: "bg-amber-500" },
        immersion_scheduled: { label: "Immersion", color: "bg-purple-400" },
        immersion_done: { label: "Validation att.", color: "bg-purple-600" },
        validated: { label: "Validé", color: "bg-emerald-500" },
        onboarding: { label: "Onboarding", color: "bg-emerald-600" },
        hired: { label: "Embauché", color: "bg-emerald-700" },
        rejected: { label: "Refusé", color: "bg-red-400" },
    };
    const entries = Object.entries(statuses).filter(([, v]) => v > 0);
    const total = entries.reduce((sum, [, v]) => sum + v, 0);
    if (!total) return null;

    return (
        <div className="card-soft p-4 md:p-5" data-testid="status-distribution">
            <div className="flex items-center justify-between mb-3">
                <div>
                    <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                        Répartition par statut
                    </div>
                    <div className="text-sm font-extrabold text-shariti-black">
                        {total} candidat{total > 1 ? "s" : ""} sur la période
                    </div>
                </div>
            </div>
            <div className="h-3 w-full rounded-full bg-shariti-cream overflow-hidden flex">
                {entries.map(([k, v]) => {
                    const meta = palette[k] || { label: k, color: "bg-slate-300" };
                    const pct = (v / total) * 100;
                    return (
                        <div
                            key={k}
                            className={`${meta.color} h-full hover:opacity-80 transition`}
                            style={{ width: `${pct}%` }}
                            title={`${meta.label} — ${v}`}
                        />
                    );
                })}
            </div>
            <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5">
                {entries.map(([k, v]) => {
                    const meta = palette[k] || { label: k, color: "bg-slate-300" };
                    return (
                        <div key={k} className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-slate-600">
                            <span className={`h-2 w-2 rounded-full ${meta.color}`} />
                            <span>{meta.label}</span>
                            <span className="font-bold tabular-nums text-shariti-black">{v}</span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function SourceDonut({ counts }) {
    const total = (counts["Site Shariti"] || 0) + (counts.Layan || 0) + (counts.other || 0);
    if (!total) return null;
    const sitePct = ((counts["Site Shariti"] || 0) / total) * 100;
    const layanPct = ((counts.Layan || 0) / total) * 100;
    const otherPct = 100 - sitePct - layanPct;
    return (
        <div className="card-soft p-4 md:p-5" data-testid="source-donut">
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">
                Par origine
            </div>
            <div className="flex items-center gap-4">
                <div
                    className="relative h-20 w-20 rounded-full shrink-0"
                    style={{
                        background: `conic-gradient(
                            #ee722e 0% ${sitePct}%,
                            #475569 ${sitePct}% ${sitePct + layanPct}%,
                            #cbd5e1 ${sitePct + layanPct}% 100%
                        )`,
                    }}
                >
                    <div className="absolute inset-2 rounded-full bg-white flex items-center justify-center">
                        <span className="text-xl font-black text-shariti-black tabular-nums">{total}</span>
                    </div>
                </div>
                <div className="flex-1 space-y-1.5">
                    <div className="flex items-center gap-2 text-xs">
                        <span className="h-2.5 w-2.5 rounded-full bg-shariti-orange" />
                        <span className="font-bold text-slate-700">Site Shariti</span>
                        <span className="ml-auto font-extrabold text-shariti-black tabular-nums">
                            {counts["Site Shariti"] || 0}
                        </span>
                    </div>
                    <div className="flex items-center gap-2 text-xs">
                        <span className="h-2.5 w-2.5 rounded-full bg-slate-600" />
                        <span className="font-bold text-slate-700">Layan / job-boards</span>
                        <span className="ml-auto font-extrabold text-shariti-black tabular-nums">
                            {counts.Layan || 0}
                        </span>
                    </div>
                    {counts.other > 0 && (
                        <div className="flex items-center gap-2 text-xs">
                            <span className="h-2.5 w-2.5 rounded-full bg-slate-300" />
                            <span className="font-bold text-slate-700">Autres</span>
                            <span className="ml-auto font-extrabold text-shariti-black tabular-nums">
                                {counts.other}
                            </span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default function TeamAnalytics() {
    const [days, setDays] = useState(30);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [tab, setTab] = useState("global"); // global | pre_rec | referents

    useEffect(() => {
        let cancelled = false;
        async function fetchData() {
            setLoading(true);
            setError(null);
            try {
                const { data } = await api.get("/dashboard/team/analytics", { params: { days } });
                if (!cancelled) setData(data);
            } catch (e) {
                if (!cancelled) setError(e.response?.data?.detail || "Erreur de chargement");
            } finally {
                if (!cancelled) setLoading(false);
            }
        }
        fetchData();
        return () => { cancelled = true; };
    }, [days]);

    const team = data?.team_totals;
    const topPreRecId = data?.pre_recruteurs?.[0]?.user_id;
    const topReferentId = data?.referents?.[0]?.user_id;

    const periodLabel = useMemo(() => {
        const p = PRESETS.find((x) => x.value === days);
        return p ? p.label : `${days} jours`;
    }, [days]);

    return (
        <div className="space-y-5 md:space-y-7" data-testid="team-analytics-page">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
                <div className="min-w-0">
                    <div className="inline-flex items-center gap-2 rounded-full bg-shariti-orange-soft text-shariti-orange px-3 py-1 text-[10px] font-bold uppercase tracking-widest mb-2">
                        <BarChart3 className="h-3 w-3" /> Analyses équipe
                    </div>
                    <h1 className="text-3xl md:text-4xl font-black tracking-tight text-shariti-black">
                        Performance équipe
                    </h1>
                    <p className="mt-1 text-sm font-medium text-slate-500">
                        Vue détaillée par pré-recruteur et référent sur les <strong>{periodLabel}</strong> écoulé{days > 1 ? "s" : ""}.
                    </p>
                </div>
                {/* Period selector */}
                <div className="inline-flex items-center gap-1 p-1 rounded-full bg-white border border-shariti-gray">
                    {PRESETS.map((p) => (
                        <button
                            key={p.value}
                            onClick={() => setDays(p.value)}
                            data-testid={`period-${p.value}`}
                            className={`px-3 h-8 rounded-full text-xs font-extrabold transition ${
                                days === p.value
                                    ? "bg-shariti-orange text-white shadow-btn"
                                    : "text-slate-600 hover:text-shariti-orange"
                            }`}
                        >
                            {p.label}
                        </button>
                    ))}
                </div>
            </div>

            {error && (
                <div className="card-soft p-6 text-center text-shariti-red font-bold" data-testid="team-analytics-error">
                    {error}
                </div>
            )}

            {loading && !data ? (
                <div className="flex items-center justify-center py-16">
                    <Loader2 className="h-8 w-8 animate-spin text-shariti-orange" />
                </div>
            ) : data ? (
                <>
                    {/* ============ TEAM TOTALS ============ */}
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
                        <KpiTile
                            label="Candidats reçus"
                            value={team.total_candidates}
                            icon={Users}
                            tone="slate"
                            subtle={`${team.total_pepites} pépites`}
                        />
                        <KpiTile
                            label="Pré-rec faits"
                            value={team.total_prerec_done}
                            icon={PhoneCall}
                            tone="orange"
                            subtle={`${team.rate_prerec}% du total`}
                        />
                        <KpiTile
                            label="Immersions réalisées"
                            value={team.total_immersions_done}
                            icon={CalendarCheck}
                            tone="purple"
                            subtle={`${team.rate_immersion_post_prerec}% post pré-rec`}
                        />
                        <KpiTile
                            label="Validés"
                            value={team.total_validated}
                            icon={CheckCircle}
                            tone="emerald"
                            subtle={`${team.rate_overall_validation}% global`}
                        />
                        <KpiTile
                            label="Vivier"
                            value={team.total_in_pool}
                            icon={BookmarkPlus}
                            tone="amber"
                            subtle="parqués"
                        />
                        <KpiTile
                            label="Pépites"
                            value={team.total_pepites}
                            icon={Flame}
                            tone="red"
                            subtle="score IA ≥ 82"
                        />
                    </div>

                    {/* ============ DISTRIBUTION CHARTS ============ */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                        <div className="lg:col-span-2">
                            <StatusBar statuses={team.by_status} />
                        </div>
                        <SourceDonut counts={team.by_source} />
                    </div>

                    {/* ============ TABS ============ */}
                    <div className="flex flex-wrap items-center gap-2 border-b border-shariti-gray">
                        <TabBtn label="Vue globale" icon={Target} active={tab === "global"} onClick={() => setTab("global")} testid="tab-global" />
                        <TabBtn
                            label={`Pré-recruteurs (${data.pre_recruteurs.length})`}
                            icon={PhoneCall}
                            active={tab === "pre_rec"}
                            onClick={() => setTab("pre_rec")}
                            testid="tab-pre-rec"
                        />
                        <TabBtn
                            label={`Référents (${data.referents.length})`}
                            icon={MapPin}
                            active={tab === "referents"}
                            onClick={() => setTab("referents")}
                            testid="tab-referents"
                        />
                    </div>

                    {/* ============ TAB CONTENT ============ */}
                    {(tab === "global" || tab === "pre_rec") && (
                        <div className="space-y-3">
                            <div className="flex items-center justify-between">
                                <h2 className="text-xl md:text-2xl font-black text-shariti-black flex items-center gap-2">
                                    <PhoneCall className="h-5 w-5 text-shariti-orange" />
                                    Pré-recruteurs
                                </h2>
                                <span className="text-xs font-bold uppercase tracking-widest text-slate-400">
                                    {data.pre_recruteurs.length} actif{data.pre_recruteurs.length > 1 ? "s" : ""}
                                </span>
                            </div>
                            {data.pre_recruteurs.length === 0 ? (
                                <div className="card-soft p-8 text-center text-slate-400 font-medium">
                                    Aucun pré-recruteur actif sur la période.
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {data.pre_recruteurs.map((p) => (
                                        <UserRow key={p.user_id} person={p} kind="pre_recruteur" isTop={p.user_id === topPreRecId && p.metrics.prerec_done > 0} />
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {(tab === "global" || tab === "referents") && (
                        <div className="space-y-3 mt-4">
                            <div className="flex items-center justify-between">
                                <h2 className="text-xl md:text-2xl font-black text-shariti-black flex items-center gap-2">
                                    <MapPin className="h-5 w-5 text-blue-600" />
                                    Référents terrain
                                </h2>
                                <span className="text-xs font-bold uppercase tracking-widest text-slate-400">
                                    {data.referents.length} actif{data.referents.length > 1 ? "s" : ""}
                                </span>
                            </div>
                            {data.referents.length === 0 ? (
                                <div className="card-soft p-8 text-center text-slate-400 font-medium">
                                    Aucun référent actif sur la période.
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {data.referents.map((r) => (
                                        <UserRow key={r.user_id} person={r} kind="referent" isTop={r.user_id === topReferentId && r.metrics.done > 0} />
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {loading && (
                        <div className="flex items-center justify-center gap-2 py-2 text-xs text-slate-400">
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            Actualisation…
                        </div>
                    )}
                </>
            ) : null}
        </div>
    );
}

function TabBtn({ label, icon: Icon, active, onClick, testid }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testid}
            className={`inline-flex items-center gap-2 px-4 h-10 -mb-px border-b-2 text-sm font-extrabold transition ${
                active
                    ? "border-shariti-orange text-shariti-orange"
                    : "border-transparent text-slate-500 hover:text-shariti-black"
            }`}
        >
            <Icon className="h-4 w-4" />
            {label}
        </button>
    );
}
